export enum PreferredLanguageEnum {
  sv = "sv",
  fi = "fi",
  en = "en",
}

export type SupportedLanguage = "sv" | "en" | "fi";

export enum PromotionTypeEnum {
  INTEREST_FREE = "INTEREST_FREE",
  AMORTISATION_FREE = "AMORTISATION_FREE",
  EU_LOAN = "EU_LOAN",
}

export enum CountryCode {
  SE = "SE",
  FI = "FI",
}

export enum Currency {
  SEK = "SEK",
  EUR = "EUR",
}

export enum AppName {
  ADMIN = "ADMIN",
  PARTNER = "PARTNER",
  CUSTOMER = "CUSTOMER",
  WIDGET = "WIDGET",
  LENDER = "LENDER",
}

export enum BidType {
  COMPANY_LOAN = "COMPANY_LOAN",
  FACTORING = "FACTORING",
  REAL_ESTATE = "REAL_ESTATE",
  LEASING = "LEASING",
  REVOLVING_CREDIT = "REVOLVING_CREDIT",
  PURCHASE_FINANCING = "PURCHASE_FINANCING",
  BUILDING_CREDIT = "BUILDING_CREDIT",
}

// Deliberately not named PaymentType, which reads as a sibling of the
// amortization-based bid types' PaymentFrequency (KREA-4400).
export enum BuildingCreditPaymentType {
  PAID_UPFRONT = "PAID_UPFRONT",
  EACH_LIFT = "EACH_LIFT",
}

export enum LeadType {
  BUSINESS_LOAN_CALCULATOR_LEAD = "BUSINESS_LOAN_CALCULATOR_LEAD",
  REAL_ESTATE_CALCULATOR_LEAD = "REAL_ESTATE_CALCULATOR_LEAD",
  LEASING_CALCULATOR_LEAD = "LEASING_CALCULATOR_LEAD",
  FACTORING_CALCULATOR_LEAD = "FACTORING_CALCULATOR_LEAD",
  PRELIMINARY_LOAN_AMOUNT_LEAD = "PRELIMINARY_LOAN_AMOUNT_LEAD",
  PRE_DRAFT_LEAD = "PRE_DRAFT_LEAD",
  ELIGIBILITY_CHECK_LEAD = "ELIGIBILITY_CHECK_LEAD",
  MANUAL = "MANUAL",
  // biome-ignore lint/style/useNamingConvention: wire value from core-service's LeadType; the siblings predate the rule's enforcement
  ADVISORY_LEAD = "ADVISORY_LEAD",
  MANUALLY_UPLOADED = "MANUALLY_UPLOADED",
}

export enum AmortizationMethod {
  ANNUITY = "ANNUITY",
  LINEAR = "LINEAR",
  FIXED = "FIXED",
  BULLET = "BULLET",
  BUILDING_CREDIT = "BUILDING_CREDIT",
}

// Wire values from core-service, matching the SDL enum `PaymentFrequency`
// exactly (verified against the live schema — there is no codegen here).
// The field is non-null server-side: the migration adds the column
// NOT NULL DEFAULT MONTHLY, so no row ever lacks a value.
export enum PaymentFrequency {
  MONTHLY = "MONTHLY",
  AT_MATURITY = "AT_MATURITY",
}

/**
 * The wire form of `AmortizationMethod`. GraphQL serialises the backend enum
 * to its member NAME, so a response carries the bare string rather than the
 * enum object — and a plain `AmortizationMethod` annotation would reject
 * `"ANNUITY"` coming off the wire.
 *
 * Derived from the enum via a template-literal type rather than re-spelling
 * the members, so adding one is a single edit here (review feedback,
 * PR #1362). Prefer this over `string` on parsed responses: it makes a typo
 * in a per-method branch a compile error.
 */
export type AmortizationMethodValue = `${AmortizationMethod}`;

export interface ITranslationObject<T = Record<string, unknown>> {
  en: T;
  sv: T;
  fi: T;
  namespace: string;
}

export interface IReviewProviderScoreData {
  provider: string;
  score: number;
  maxScore: number;
  numberOfReviews: string;
  linkToProviderReviews: string;
}

export enum CallbackPageType {
  BANKING_DATA = "banking-data",

  // Whitelisted url for FI Bank ID callback handling. Dont change lightly.
  // See Nets FI BankID in 1password for more details.
  FI_BANK_ID = "fi-bank-id",
}

/** Compat alias: new code uses `AppEnvironment` from `@krea/shared/config`. */
export enum EnvironmentEnum {
  PRODUCTION = "production",
  STAGING = "staging",
  DEVELOPMENT = "development",
  LOCAL_DOCKER = "local-docker",
  TEST = "test",
}

export interface IPreliminaryLoanAmountData {
  estimatedMaxLoanAmount: number;
  probabilityLoanApproved: string;
  useEdgeCaseLogic: boolean;
  estimatedMinLoanAmount: number;
  conversionValue: number | null;
  customerCompanyId?: string | number | null;
}
